import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { loginRequest, msalConfig } from './authConfig'
import { Toaster } from './components/ui/sonner'
import PlaceholderPage from './pages/PlaceholderPage'
import 'react-day-picker/dist/style.css'
import './index.css'

const App = lazy(() => import('./App.tsx'))
const AuthGate = lazy(() => import('./components/AuthGate'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const NetworkSearchPage = lazy(() => import('./pages/NetworkSearchPage'))
const OrganizationsPage = lazy(() => import('./pages/OrganizationsPage'))
const OrganizationDetailPage = lazy(() => import('./pages/OrganizationDetailPage'))
const OrganizationGroupCreatePage = lazy(() => import('./pages/OrganizationGroupCreatePage'))
const OrganizationEditPage = lazy(() => import('./pages/OrganizationEditPage'))
const GroupsPage = lazy(() => import('./pages/GroupsPage'))
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage'))
const GroupLocationCreatePage = lazy(() => import('./pages/GroupLocationCreatePage'))
const ProvidersPage = lazy(() => import('./pages/ProvidersPage'))
const ProviderDetailPage = lazy(() => import('./pages/ProviderDetailPage'))
const ProviderLocationCreatePage = lazy(() => import('./pages/ProviderLocationCreatePage'))
const ProviderSpecialtyEditPage = lazy(() => import('./pages/ProviderSpecialtyEditPage'))
const ProviderEditPage = lazy(() => import('./pages/ProviderEditPage'))
const GroupEditPath = lazy(() => import('./pages/GroupEditPath'))
const ProviderUpdateEditPage = lazy(() => import('./pages/ProviderUpdateEditPage'))
const AddressEditPage = lazy(() => import('./pages/AddressEditPage'))
const AddressTermPage = lazy(() => import('./pages/AddressTermPage'))
const UpdateFormPage = lazy(() => import('./pages/UpdateFormPage'))
const AdministrationPage = lazy(() => import('./pages/AdministrationPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const UncredentialedProvidersPage = lazy(() => import('./pages/UncredentialedProvidersPage'))
const StagedChangesPage = lazy(() => import('./pages/StagedChangesPage'))
const StagedChangeDetailPage = lazy(() => import('./pages/StagedChangeDetailPage'))

const msalInstance = new PublicClientApplication(msalConfig)

async function bootstrap() {
  try {
    await msalInstance.initialize()
    const redirectResult = await msalInstance.handleRedirectPromise()
    const activeAccount =
      redirectResult?.account ??
      msalInstance.getActiveAccount() ??
      msalInstance.getAllAccounts()[0] ??
      null

    if (activeAccount) {
      msalInstance.setActiveAccount(activeAccount)
    }
  } catch (error) {
    console.error('MSAL redirect handling failed', error)

    if (shouldRedirectToLogin(error)) {
      void msalInstance.loginRedirect(loginRequest)
      return
    }
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-600">Loading...</div>}>
            <Routes>
              <Route
                path="/"
                element={
                  <AuthGate>
                    <App />
                  </AuthGate>
                }
              >
                <Route index element={<ProvidersPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="network-search" element={<NetworkSearchPage />} />
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="organizations/new" element={<OrganizationEditPage />} />
                <Route path="organizations/:identifier" element={<OrganizationDetailPage />} />
                <Route path="organizations/:identifier/groups/new" element={<OrganizationGroupCreatePage />} />
                <Route path="organizations/:identifier/edit" element={<OrganizationEditPage />} />
                <Route path="groups" element={<GroupsPage />} />
                <Route path="groups/:providerGroupId" element={<GroupDetailPage />} />
                <Route path="groups/:providerGroupId/address" element={<AddressEditPage />} />
                <Route path="groups/:providerGroupId/address/:id" element={<AddressEditPage />} />
                <Route path="groups/:providerGroupId/locations/new" element={<GroupLocationCreatePage />} />
                <Route path="providers" element={<ProvidersPage />} />
                <Route path="providers/new/detail/edit" element={<ProviderEditPage />} />
                <Route path="providers/:providerEntityId/locations/new" element={<ProviderLocationCreatePage />} />
                <Route path="providers/:providerEntityId/detail" element={<ProviderDetailPage />} />
                <Route path="providers/:npi/detail/specialties/new" element={<ProviderSpecialtyEditPage />} />
              <Route path="providers/:npi/detail/specialties/:id/edit" element={<ProviderSpecialtyEditPage />} />
                <Route path="providers/:npi/detail/edit" element={<ProviderEditPage />} />
                <Route path="addresses/:kind/:id/edit" element={<AddressEditPage />} />
                <Route path="addresses/:id/edit" element={<AddressEditPage />} />
                <Route path="location/:id/edit" element={<AddressEditPage />} />
                <Route path="addresses/:kind/:id/term" element={<AddressTermPage />} />
                <Route path="contracts" element={<PlaceholderPage title="Contracts" />} />
                <Route path="reports" element={<PlaceholderPage title="Reports" subtitle="Reporting tools will appear here." />} />
                <Route path="administration" element={<AdministrationPage />} />
                <Route path="administration/settings" element={<SettingsPage />} />
                <Route path="administration/uncredentialed-providers" element={<UncredentialedProvidersPage />} />
                <Route path="administration/staged-changes" element={<StagedChangesPage />} />
                <Route path="administration/staged-changes/:id" element={<StagedChangeDetailPage />} />
                <Route path="groups/:providerGroupId/edit" element={<GroupEditPath />} />
                <Route path="provider-updates/:id/edit" element={<ProviderUpdateEditPage />} />
              </Route>
              <Route
                path="/auth-preview"
                element={
                  <AuthGate autoRedirect={false} showPromptWhenAuthenticated>
                    <div />
                  </AuthGate>
                }
              />
              <Route path="/Provider-Update" element={<UpdateFormPage />} />
            </Routes>
          </Suspense>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>,
  )
}

function shouldRedirectToLogin(error: unknown) {
  if (error instanceof InteractionRequiredAuthError) {
    return true
  }

  if (!error || typeof error !== 'object') {
    return false
  }

  const authError = error as { errorCode?: string; name?: string }
  return (
    authError.errorCode === 'no_token_request_cache_error' ||
    authError.errorCode === 'timed_out' ||
    authError.name === 'BrowserAuthError'
  )
}

void bootstrap()
